import { Users, Play } from 'lucide-react';
import type { Product } from './types';
import { normalizeProductPricing } from './pricingNormalization';

export const workforceManagementSystem: Product = normalizeProductPricing({
  id: 'workforce-management-system',
  title: 'Enterprise Workforce Management System',
  subtitle: 'AI-Powered Scheduling, Forecasting & Real-Time Workforce Optimisation',
  description: 'Revolutionary workforce management platform combining AI-powered forecasting with 98% accuracy, intelligent scheduling, real-time adherence monitoring, and gamified performance management for BPO, call centres, and enterprise contact centres worldwide.',
  icon: Users,
  image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  category: 'BPO & Contact Centre',
  status: 'Live',
  businessTypes: ['#bpo', '#call-centre', '#contact-centre', '#workforce', '#scheduling'],
  features: [
    // AI-Powered Forecasting & Planning (8 features)
    'Machine Learning Forecasting with 98% Accuracy',
    'Multi-Channel Volume Prediction (Voice, Chat, Email, Social)',
    'Historical Pattern Analysis & Trend Detection',
    'Seasonal Adjustment & Holiday Forecasting',
    'What-If Scenario Planning & Modelling',
    'Capacity Planning & Resource Allocation',
    'Shrinkage & Attrition Forecasting',
    'Real-Time Forecast Variance Alerts',

    // Intelligent Scheduling (8 features)
    'AI-Optimised Shift Generation & Assignment',
    'Preference-Based Rostering & Shift Bidding',
    'Skill-Based Routing & Agent Matching',
    'Multi-Site & Timezone Coordination',
    'Automated Break & Lunch Scheduling',
    'Overtime & Undertime Management',
    'Shift Swap & Trade Management Portal',
    'Dynamic Schedule Adjustment & Reforecasting',

    // Real-Time Adherence & Monitoring (7 features)
    'Live Adherence Tracking Dashboard',
    'Automated Variance Alerts & Notifications',
    'Intraday Timeline Visualisation',
    'Agent State Monitoring & Reporting',
    'Exception Management & Approval Workflow',
    'Real-Time Service Level Tracking',
    'Instant Schedule Change Broadcasting',

    // Mobile & Agent Self-Service (7 features)
    'Native iOS & Android Mobile Apps',
    'Push Notification System for Schedule Updates',
    'Mobile Shift Swap & Time-Off Requests',
    'Schedule View & Availability Management',
    'Team Chat & Communication Tools',
    'Personal Performance Dashboard',
    'Offline Access & Background Sync',

    // Performance & Gamification (8 features)
    'Comprehensive Performance Analytics',
    'Gamified KPI Tournaments & Leagues',
    'Achievement Badges & Leaderboards',
    'Coaching Insights & Development Plans',
    'Goal Setting & Progress Tracking',
    'Peer Recognition & Reward Systems',
    'Performance Trend Analysis',
    'Team & Individual Scorecards',

    // Integration & Automation (6 features)
    'HRIS Integration (Workday, SAP, Oracle)',
    'Payroll System Connectivity (ADP, Kronos)',
    'Telephony & ACD Integration',
    'Microsoft Teams & Slack Integration',
    'API-First Architecture for Custom Integrations',
    'Automated Payroll Data Export',

    // Enterprise Security & Compliance (8 features)
    'SOC 2 Type II Certified Infrastructure',
    'ISO 27001 Compliant Security Controls',
    'GDPR & Data Protection Compliance',
    'Role-Based Access Control (RBAC)',
    'Comprehensive Audit Trail Logging',
    'SSO & Multi-Factor Authentication',
    'Data Encryption at Rest & In Transit',
    'Cloud, Hybrid & On-Premises Deployment Options',
  ],
  pricing: { setup: 26600, monthly: 865 },
  color: 'from-emerald-600 to-teal-700',
  demoConfig: {
    type: 'interactive',
    url: '/workforce-management/demo',
    title: 'Experience the Complete Platform',
    description: 'Explore our AI-powered workforce management system with forecasting, scheduling, and real-time monitoring',
    buttonText: 'Launch WFM Demo',
    icon: Play,
    buttonColor: 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700',
  },
});

